import DonLinear from "@/assets/icons/don-linear.svg?react";
import IconGift from "@/assets/icons/gift.svg?react";
import IconCheck from "@/assets/icons/check.svg?react";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import Image from "@/components/base/Image.tsx";
import { formatNumber } from "@/shared/utils";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Voucher } from "../api";

interface VoucherCardProps {
  voucher: Voucher;
  onRedeem: (id: string) => void;
  isRedeeming: boolean;
  onShow?: (voucher: Voucher) => void;
}

function StatusPill({
  tone,
  label,
  withCheck = false,
  pulse = false,
}: {
  tone: "orange" | "green" | "gray" | "red";
  label: string;
  withCheck?: boolean;
  pulse?: boolean;
}) {
  const toneClass =
    tone === "orange"
      ? "bg-orange-100/15 text-orange-100"
      : tone === "green"
      ? "bg-green/15 text-green"
      : tone === "red"
      ? "bg-red/15 text-red"
      : "bg-white/08 text-gray-1";

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold leading-none ${toneClass} ${pulse ? "animate-pulse" : ""}`}
    >
      {withCheck && <IconCheck className="size-3" />}
      {label}
    </span>
  );
}

function getUrgentExpiry(
  dateString: string | null | undefined,
  context: "offer" | "use",
  t: (k: string, opts?: Record<string, unknown>) => string
):
  | { tone: "orange" | "red"; label: string; pulse: boolean }
  | null {
  if (!dateString) return null;
  const end = dayjs(dateString);
  if (!end.isValid()) return null;

  const now = dayjs();
  if (end.diff(now) <= 0) return null;

  const diffHours = end.diff(now, "hour");
  const diffDays = Math.ceil(diffHours / 24);
  // Only surface urgent expiries (≤ 3 days). Longer windows are noise.
  if (diffDays > 3) return null;

  if (diffHours < 24) {
    return {
      tone: "red",
      label:
        context === "offer"
          ? t("voucher_offer_ends_today")
          : t("voucher_use_ends_today"),
      pulse: true,
    };
  }
  return {
    tone: diffDays <= 1 ? "red" : "orange",
    label:
      context === "offer"
        ? t("voucher_offer_days_left", {
            count: diffDays,
            defaultValue: `Aksiyaga ${diffDays} kun`,
          }) || `Aksiyaga ${diffDays} kun`
        : t("voucher_use_days_left", {
            count: diffDays,
            defaultValue: `${diffDays} kun qoldi`,
          }) || `${diffDays} kun qoldi`,
    pulse: diffDays <= 1,
  };
}

export default function VoucherCard({
  voucher,
  onRedeem,
  isRedeeming,
  onShow,
}: VoucherCardProps) {
  const { t } = useTranslation();
  const status = voucher.status;
  const isAvailable = status === "AVAILABLE";
  const isRedeemed = status === "REDEEMED";
  const isUsed = status === "USED";
  const isExpired = status === "EXPIRED";
  const isSoldOut = status === "SOLD_OUT";
  const isDimmed = isUsed || isExpired || isSoldOut;
  const canTap = isAvailable || isRedeemed || isSoldOut;

  const expiry = isRedeemed
    ? getUrgentExpiry(voucher.redeem_valid_until, "use", t)
    : isAvailable
    ? getUrgentExpiry(voucher.end_date, "offer", t)
    : null;

  return (
    <div
      data-voucher-id={voucher.id}
      role={canTap ? "button" : undefined}
      tabIndex={canTap ? 0 : -1}
      aria-disabled={!canTap}
      onClick={(e) => {
        e.stopPropagation();
        if (canTap) onShow?.(voucher);
      }}
      onKeyDown={(e) => {
        if (canTap && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onShow?.(voucher);
        }
      }}
      className={`w-full text-left rounded-xl border border-white/[0.04] bg-gray-4 overflow-hidden flex flex-col transition-[transform,background-color] duration-200 ${
        canTap ? "active:scale-[0.985] active:bg-white/[0.04] cursor-pointer" : ""
      } ${isDimmed ? "opacity-70" : ""}`}
    >
      {/* Banner image */}
      <div className="relative w-full aspect-[4/3] bg-white/[0.04] overflow-hidden">
        {voucher.icon ? (
          <div className="absolute inset-0">
            <Image
              urls={voucher.icon}
              size="small"
              imgClassName="w-full h-full object-cover"
              wrapperClassName="w-full h-full"
              alt={voucher.title}
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex-center">
            <IconGift
              className="size-10 text-orange-100/60"
              strokeWidth="1.5"
            />
          </div>
        )}
        {voucher.is_hot && (
          <span className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-red text-[10px] font-bold text-white leading-none uppercase tracking-wide shadow-[0_2px_8px_rgba(255,53,53,0.4)]">
            HOT
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-2.5 flex flex-col flex-1 gap-2">
        <h3
          className={`text-sm font-semibold leading-130 line-clamp-2 ${
            isDimmed ? "text-gray-1" : "text-white"
          }`}
        >
          {voucher.title}
        </h3>

        {/* Status/urgency pill — only when meaningful (Sizniki badge moves to footer for isRedeemed) */}
        {!isRedeemed && (isUsed || isExpired || isSoldOut || expiry) && (
          <div className="flex">
            {isUsed && (
              <StatusPill tone="gray" label={t("voucher_used")} withCheck />
            )}
            {isExpired && (
              <StatusPill tone="red" label={t("voucher_expired")} />
            )}
            {isSoldOut && (
              <StatusPill
                tone="gray"
                label={t("voucher_sold_out") || "Tugadi"}
              />
            )}
            {expiry && (
              <StatusPill
                tone={expiry.tone}
                label={expiry.label}
                pulse={expiry.pulse}
              />
            )}
          </div>
        )}

        {/* Footer — Sizniki badge + Ko'rish button on the same row */}
        {isRedeemed && (
          <div className="flex items-center justify-between gap-2 mt-auto pt-1">
            <StatusPill tone="green" label={t("voucher_yours")} withCheck />
            <BaseButton
              text={t("show")}
              variant="secondary"
              className="!py-1 !px-2.5 !text-[11px] !rounded-full shrink-0"
              onClick={(e) => {
                e?.stopPropagation?.();
                onShow?.(voucher);
              }}
              vibrate
              vibrationType="light"
            />
          </div>
        )}

        {/* Footer — price + CTA for available / sold-out */}
        {(isAvailable || isSoldOut) && (
          <div className="flex items-center justify-between gap-2 mt-auto pt-1">
            <div className="flex items-center gap-0.5 min-w-0">
              <DonLinear
                className={`size-4 shrink-0 ${isSoldOut ? "opacity-50" : ""}`}
              />
              <span
                className={`font-bold text-sm leading-130 ${
                  isSoldOut ? "text-gray-1" : "text-orange-100"
                }`}
              >
                {formatNumber(voucher.don_cost)}
              </span>
            </div>

            {isAvailable && (
              <BaseButton
                text={t("redeem")}
                variant="premium"
                className="!py-1 !px-2.5 !text-[11px] !rounded-full shrink-0 !text-dark"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  onRedeem(voucher.id);
                }}
                isLoading={isRedeeming}
                vibrate
                vibrationType="light"
              />
            )}
            {isSoldOut && (
              <BaseButton
                text={t("redeem")}
                variant="secondary"
                className="!py-1 !px-2.5 !text-[11px] !rounded-full shrink-0 opacity-50"
                disabled
                vibrate={false}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
